import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  experienceDesignApi,
  type AccessibilitySnapshot,
  type CognitiveLoadSnapshot,
  type CopywritingSnapshot,
  type DesignQASnapshot,
  type ExperienceDashboardSnapshot,
  type MotionDesignSnapshot,
  type PrinciplesSnapshot,
  type RecommendationUXSnapshot,
  type UsabilityMetricsSnapshot,
  type VisualDesignSnapshot,
} from '../../api/experience-design-xdb001-runtime';

type Tab = 'overview' | 'principles' | 'visual' | 'accessibility' | 'cognitive' | 'motion' | 'design_qa' | 'usability' | 'recommendations' | 'copywriting';

export function ExperienceDesignPage() {
  const [tab, setTab] = useState<Tab>('overview');
  const [dashboard, setDashboard] = useState<ExperienceDashboardSnapshot | null>(null);
  const [principles, setPrinciples] = useState<PrinciplesSnapshot | null>(null);
  const [visual, setVisual] = useState<VisualDesignSnapshot | null>(null);
  const [accessibility, setAccessibility] = useState<AccessibilitySnapshot | null>(null);
  const [cognitive, setCognitive] = useState<CognitiveLoadSnapshot | null>(null);
  const [motion, setMotion] = useState<MotionDesignSnapshot | null>(null);
  const [designQA, setDesignQA] = useState<DesignQASnapshot | null>(null);
  const [usability, setUsability] = useState<UsabilityMetricsSnapshot | null>(null);
  const [recommendations, setRecommendations] = useState<RecommendationUXSnapshot | null>(null);
  const [copywriting, setCopywriting] = useState<CopywritingSnapshot | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const card = 'border-violet-200 bg-white dark:border-violet-900/40 dark:bg-zinc-900';
  const muted = 'text-slate-500 dark:text-zinc-400';
  const accent = 'text-violet-700 dark:text-violet-300';
  const shell = 'bg-violet-50/20 dark:bg-zinc-950';

  const refresh = useCallback(async () => {
    setError(null); setLoading(true);
    try {
      const [dash, prin, vis, a11y, cog, mot, qa, use, rec, copy] = await Promise.all([
        experienceDesignApi.dashboard(), experienceDesignApi.principles(),
        experienceDesignApi.visualDesign(), experienceDesignApi.accessibility(),
        experienceDesignApi.cognitiveLoad(), experienceDesignApi.motion(),
        experienceDesignApi.designQA(), experienceDesignApi.usability(),
        experienceDesignApi.recommendations(), experienceDesignApi.copywriting(),
      ]);
      setDashboard(dash); setPrinciples(prin); setVisual(vis); setAccessibility(a11y);
      setCognitive(cog); setMotion(mot); setDesignQA(qa); setUsability(use);
      setRecommendations(rec); setCopywriting(copy);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load Experience Design');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Overview' }, { id: 'principles', label: 'Principles' },
    { id: 'visual', label: 'Visual Design' }, { id: 'accessibility', label: 'Accessibility' },
    { id: 'cognitive', label: 'Cognitive Load' }, { id: 'motion', label: 'Motion' },
    { id: 'design_qa', label: 'Design QA' }, { id: 'usability', label: 'Usability' },
    { id: 'recommendations', label: 'Recommendations UX' }, { id: 'copywriting', label: 'Copywriting' },
  ];

  return (
    <div className={`space-y-4 rounded-xl p-1 ${shell}`} role="main">
      <header className={`rounded-xl border p-4 ${card}`}>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className={`text-xs font-semibold uppercase tracking-wide ${accent}`}>XDB-000001 · Experience Design Bible</p>
            <h1 className="text-xl font-semibold">Human Experience Command Center</h1>
            {dashboard && (
              <div className={`mt-2 flex flex-wrap gap-x-4 text-sm ${muted}`}>
                <span>Experience Score <strong className={accent}>{dashboard.experience_score}</strong></span>
                <span>WCAG <strong className={accent}>{dashboard.wcag_compliance_pct}%</strong></span>
                <span>Design QA <strong className={accent}>{dashboard.design_qa_pass_rate_pct}%</strong></span>
                <span>Pages Audited <strong className={accent}>{dashboard.pages_audited}</strong></span>
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <Link to="/customer-value" className={`rounded-lg border px-3 py-1.5 text-sm ${card} ${accent}`}>Customer Value</Link>
            <Link to="/platform/experience-design-xdb001" className={`rounded-lg border px-3 py-1.5 text-sm ${card} ${accent}`}>Sprint Center</Link>
          </div>
        </div>
      </header>

      {error && <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      {loading && <p className={`text-sm ${muted}`}>Loading experience design standards…</p>}

      <div className="flex flex-wrap gap-2">
        {tabs.map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)} className={`rounded-lg px-3 py-1.5 text-sm ${tab === t.id ? 'bg-violet-600 text-white' : `${card} border`}`}>{t.label}</button>
        ))}
      </div>

      {tab === 'overview' && dashboard && (
        <div className="grid gap-3 sm:grid-cols-4">
          {[
            { label: 'Experience Score', value: dashboard.experience_score },
            { label: 'Consistency', value: dashboard.consistency_score },
            { label: 'Accessibility', value: dashboard.accessibility_score },
            { label: 'Cognitive Load', value: dashboard.cognitive_load_score },
            { label: 'Motion', value: dashboard.motion_score },
            { label: 'Performance Perception', value: dashboard.performance_score },
            { label: 'Design QA Pass Rate', value: `${dashboard.design_qa_pass_rate_pct}%` },
            { label: 'Trend', value: dashboard.improvement_trend },
          ].map((m) => (
            <div key={m.label} className={`rounded-xl border p-4 ${card}`}>
              <p className={`text-xs uppercase ${muted}`}>{m.label}</p>
              <p className="text-2xl font-semibold capitalize">{m.value}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'principles' && principles && (
        <div className="grid gap-3 sm:grid-cols-2">
          <div className={`rounded-xl border p-4 ${card}`}>
            <p className={`text-xs uppercase ${accent}`}>The product SHALL feel</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {principles.feel_like.map((f) => (
                <span key={f} className="rounded-full bg-violet-100 px-2 py-0.5 text-xs text-violet-800 dark:bg-violet-900/40 dark:text-violet-200">{f}</span>
              ))}
            </div>
          </div>
          <div className={`rounded-xl border p-4 ${card}`}>
            <p className={`text-xs uppercase ${muted}`}>Never</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {principles.never_like.map((f) => (
                <span key={f} className="rounded-full bg-red-50 px-2 py-0.5 text-xs text-red-700 dark:bg-red-900/30 dark:text-red-200">{f}</span>
              ))}
            </div>
          </div>
          <div className={`col-span-full rounded-xl border p-4 ${card}`}>
            <p className={`text-xs uppercase ${muted}`}>Product Personality</p>
            <p className="mt-1 font-medium">{principles.product_persona}</p>
          </div>
        </div>
      )}

      {tab === 'visual' && visual && (
        <div className="grid gap-3 sm:grid-cols-3">
          {[
            { label: 'Typography', value: visual.typography_scale },
            { label: 'Spacing Unit', value: visual.base_spacing_unit },
            { label: 'Grid Columns', value: visual.grid_columns },
            { label: 'Corner Radius', value: visual.corner_radius },
            { label: 'Primary Color', value: visual.primary_color },
            { label: 'Dark Mode', value: visual.dark_mode_supported ? 'Supported' : 'No' },
            { label: 'Hierarchy Score', value: visual.visual_hierarchy_score },
            { label: 'Whitespace Score', value: visual.whitespace_score },
          ].map((m) => (
            <div key={m.label} className={`rounded-xl border p-4 ${card}`}>
              <p className={`text-xs uppercase ${muted}`}>{m.label}</p>
              <p className="text-lg font-semibold">{m.value}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'accessibility' && accessibility && (
        <div className="grid gap-3 sm:grid-cols-3">
          {[
            { label: 'WCAG Level', value: accessibility.wcag_level },
            { label: 'Compliance', value: `${accessibility.compliance_pct}%` },
            { label: 'Keyboard Only', value: accessibility.keyboard_only_pass ? 'Pass' : 'Fail' },
            { label: 'Screen Reader', value: accessibility.screen_reader_pass ? 'Pass' : 'Fail' },
            { label: 'High Contrast', value: accessibility.high_contrast_pass ? 'Pass' : 'Fail' },
            { label: 'Reduced Motion', value: accessibility.reduced_motion_pass ? 'Pass' : 'Fail' },
            { label: 'Accessible Forms', value: `${accessibility.accessible_forms_pct}%` },
            { label: 'Accessible Charts', value: `${accessibility.accessible_charts_pct}%` },
          ].map((m) => (
            <div key={m.label} className={`rounded-xl border p-4 ${card}`}>
              <p className={`text-xs uppercase ${muted}`}>{m.label}</p>
              <p className="text-lg font-semibold">{m.value}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'cognitive' && cognitive && (
        <div className="grid gap-3 sm:grid-cols-3">
          {[
            { label: 'Avg Decisions/Page', value: cognitive.avg_decisions_per_page },
            { label: 'Avg Clicks to Goal', value: cognitive.avg_clicks_to_goal },
            { label: 'Context Switches', value: cognitive.context_switches_per_session },
            { label: 'Load Score', value: cognitive.load_score },
            { label: 'Recognition over Recall', value: cognitive.recognition_over_recall ? 'Yes' : 'No' },
          ].map((m) => (
            <div key={m.label} className={`rounded-xl border p-4 ${card}`}>
              <p className={`text-xs uppercase ${muted}`}>{m.label}</p>
              <p className="text-lg font-semibold">{m.value}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'motion' && motion && (
        <div className="grid gap-3 sm:grid-cols-3">
          {[
            { label: 'Default Duration', value: `${motion.default_duration_ms}ms` },
            { label: 'Easing', value: motion.default_easing },
            { label: 'Page Transition', value: `${motion.page_transition_ms}ms` },
            { label: 'Reduce Motion', value: motion.reduce_motion_supported ? 'Supported' : 'No' },
            { label: 'Skeleton Animation', value: motion.skeleton_animation ? 'Yes' : 'No' },
            { label: 'Compliance', value: `${motion.motion_compliance_pct}%` },
          ].map((m) => (
            <div key={m.label} className={`rounded-xl border p-4 ${card}`}>
              <p className={`text-xs uppercase ${muted}`}>{m.label}</p>
              <p className="text-lg font-semibold">{m.value}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'design_qa' && designQA && (
        <div className="grid gap-3 sm:grid-cols-3">
          {[
            { label: 'Pages Passed', value: designQA.pages_passed },
            { label: 'Pages Failed', value: designQA.pages_failed },
            { label: 'Pass Rate', value: `${designQA.pass_rate_pct}%` },
            { label: 'Consistency Issues', value: designQA.consistency_issues },
            { label: 'Alignment Issues', value: designQA.alignment_issues },
            { label: 'Accessibility Issues', value: designQA.accessibility_issues },
          ].map((m) => (
            <div key={m.label} className={`rounded-xl border p-4 ${card}`}>
              <p className={`text-xs uppercase ${muted}`}>{m.label}</p>
              <p className="text-lg font-semibold">{m.value}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'usability' && usability && (
        <div className="grid gap-3 sm:grid-cols-4">
          {[
            { label: 'Task Completion', value: `${usability.task_completion_pct}%` },
            { label: 'Time to Complete', value: `${usability.avg_time_to_complete_sec}s` },
            { label: 'Error Rate', value: `${usability.error_rate_pct}%` },
            { label: 'NPS', value: usability.nps },
            { label: 'CSAT', value: usability.csat },
            { label: 'Feature Usage', value: `${usability.feature_usage_pct}%` },
            { label: 'Navigation Efficiency', value: usability.navigation_efficiency },
          ].map((m) => (
            <div key={m.label} className={`rounded-xl border p-4 ${card}`}>
              <p className={`text-xs uppercase ${muted}`}>{m.label}</p>
              <p className="text-lg font-semibold">{m.value}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'recommendations' && recommendations && (
        <div className={`rounded-xl border p-4 ${card}`}>
          <div className="grid gap-3 sm:grid-cols-3">
            {[
              { label: 'Business Value Visible', ok: recommendations.business_value_visible },
              { label: 'Evidence Visible', ok: recommendations.evidence_visible },
              { label: 'Confidence Visible', ok: recommendations.confidence_visible },
              { label: 'Approve/Reject Actions', ok: recommendations.approve_reject_actions },
              { label: 'Explain Action', ok: recommendations.explain_action },
              { label: 'Expected ROI Visible', ok: recommendations.expected_roi_visible },
            ].map((m) => (
              <div key={m.label} className="flex items-center justify-between">
                <span className={`text-sm ${muted}`}>{m.label}</span>
                <span className={m.ok ? 'text-violet-600' : 'text-red-600'}>{m.ok ? '✓' : '✗'}</span>
              </div>
            ))}
          </div>
          <p className={`mt-4 text-sm ${muted}`}>Adoption Rate: <strong className={accent}>{recommendations.adoption_rate_pct}%</strong></p>
        </div>
      )}

      {tab === 'copywriting' && copywriting && (
        <div className={`rounded-xl border p-4 ${card}`}>
          <p className={`text-xs uppercase ${muted}`}>Voice</p>
          <p className="mt-1 text-lg font-medium">{copywriting.voice}</p>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <div>
              <p className={`text-xs uppercase ${accent}`}>Tone</p>
              <div className="mt-1 flex flex-wrap gap-1">
                {copywriting.tone_attributes.map((t) => (
                  <span key={t} className="rounded bg-violet-100 px-2 py-0.5 text-xs dark:bg-violet-900/40">{t}</span>
                ))}
              </div>
            </div>
            <div>
              <p className={`text-xs uppercase ${muted}`}>Avoid</p>
              <div className="mt-1 flex flex-wrap gap-1">
                {copywriting.avoid_attributes.map((t) => (
                  <span key={t} className="rounded bg-red-50 px-2 py-0.5 text-xs dark:bg-red-900/30">{t}</span>
                ))}
              </div>
            </div>
          </div>
          <p className={`mt-4 text-sm ${muted}`}>Copy Consistency Score: <strong>{copywriting.copy_consistency_score}</strong></p>
        </div>
      )}
    </div>
  );
}
